import { ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';

export interface Crumb {
  label: string;
  to?: string;
}

/** Okruszki (Body/S muted, separator chevron). Ostatni element bez linku. */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Okruszki" className={cn('flex items-center gap-1.5 text-body-s text-fg-muted', className)}>
      {items.map((item, index) => (
        <Fragment key={`${item.to ?? ''}|${item.label}`}>
          {index > 0 && <ChevronRight size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />}
          {item.to ? (
            <Link
              to={item.to}
              className="rounded-xs hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              {item.label}
            </Link>
          ) : (
            <span aria-current={index === items.length - 1 ? 'page' : undefined}>{item.label}</span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

/**
 * Nagłówek strony (DESIGN §4): okruszki, tytuł H3 (mobile H2), linia meta, akcje po prawej
 * (maksymalnie jeden Primary). `aside` = element obok tytułu (np. Segmented „Wpisy / Raport”).
 * `detail` = strona szczegółów (profil klienta, projekt, zasób): na mobile tytuł H3 jak w Figmie 05 mobile
 * (długie nazwy w H2 łamały się na kilka linii).
 */
export function PageHeader({
  title,
  meta,
  breadcrumbs,
  actions,
  aside,
  detail = false,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  aside?: ReactNode;
  detail?: boolean;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-col gap-2', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} className="mb-1" />}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Przegląd Fali 11: na telefonie dodatek (np. przełącznik widoków) zawija się pod tytuł zamiast na niego wchodzić */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3 md:flex-nowrap">
            <h1 className={cn('min-w-0 text-fg md:truncate md:text-h3', detail ? 'text-h3' : 'text-h2')}>{title}</h1>
            {aside}
          </div>
          {meta && <div className="text-body-s text-fg-secondary">{meta}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** Nagłówek sekcji na stronie („Wymaga Ciebie”, „Plan na dziś”): Body/S Medium muted, opcjonalna akcja po prawej. */
export function SectionHeader({
  title,
  action,
  tone = 'muted',
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  /** `strong`: ciemny nagłówek (profil klienta: „Projekty i usługi”). */
  tone?: 'muted' | 'strong';
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-3 pb-2', className)}>
      <h2 className={cn('text-body-s font-medium', tone === 'muted' ? 'text-fg-muted' : 'font-semibold text-fg')}>
        {title}
      </h2>
      {action}
    </div>
  );
}
